import { Box, Button, Stack } from "@mui/material";
import { Grid, InputAdornment, MenuItem, Tooltip } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import FileInfoTable from "./fileInfoTable";
import { IntegrationEntity, RemoteFileInfo, RemoteStreamLoadFile, connectionProfileType} from "../../types/types";
import { Loader } from "../../components/loader/Loader";
import { useDispatch, useSelector } from "react-redux";
import { IntegrationActions } from "../../stateManagement/actions/integrationDashboardActions";
import { GridColDef, useGridApiRef } from "@mui/x-data-grid";
import { GenericInput } from "../../components/inputs/GenericInput";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { isUndefinedorEmpty } from "../transformer/UnregisteredFunctions";
import { encryptPassword, getFileMetaFromProfile } from "../../services/integrationService";

export default function FileFilterPage(props: {
  activeProfile:connectionProfileType,
  updateRemoteFile: React.Dispatch<React.SetStateAction<RemoteStreamLoadFile>>,
  increment:()=>void, 
  decrement:()=>void,
  DelimiterOptions:{[key:string]:string}[],
  EncodingOptions:string[],
  entitiesList:IntegrationEntity[],
  fileFilterColumnsConfig:GridColDef[],
}) {
  const initialPassKey:string = useSelector((state: any) => state.integrationRegistry.passKey)
  const [entity,setEntity] = useState<string>(useSelector((state: any) => state.integrationRegistry.entityName));
  const [delimiter,setDelimiter] = useState<string>(useSelector((state: any) => state.integrationRegistry.delimiter));
  const [encoding,setEncoding] = useState<string>(useSelector((state: any) => state.integrationRegistry.encoding));
  const [searchText, setSearchText] = useState<string>(useSelector((state: any) => state.integrationRegistry.startsWith));
  const [passkey, setPasskey] = useState<string>(initialPassKey);

  const [loader, setLoader] = useState<boolean>(true);

  const apiRef = useGridApiRef();

  const [fileMetaDataList, setFileMetaDataList] = useState<RemoteFileInfo[]>([]);
    
  const fetchFileMetaDataList = useCallback(async () => {
      if(!isUndefinedorEmpty(props.activeProfile.name)){
          const fileMetaDataList = await getFileMetaFromProfile(props.activeProfile.name);
          setFileMetaDataList(fileMetaDataList);
          setLoader(false);
      }
  }, [props.activeProfile.name, setFileMetaDataList]);

  useEffect(() => {
    fetchFileMetaDataList();
  },[fetchFileMetaDataList])

  const dispatch = useDispatch();
  
  function handleClick(){
    const encPass = initialPassKey !== passkey ? encryptPassword(passkey) : passkey;
    dispatch(IntegrationActions.setFileFilterPageInfo({
      passKey: encPass,
      entityName: entity,
      startsWith: searchText,
      encoding,
      delimiter
    }));
    const rowIds = apiRef.current.getRowIdFromRowIndex(0);
    const row = apiRef.current.getRow(rowIds);
    if(isUndefinedorEmpty(row)){
      alert('Please select a file to proceed.')
      return;
    }
    props.updateRemoteFile(prevobj=>{return {
      ...prevobj,
      fileName: row.name,
      name : row.name,
      size : row.size,
      lastmodified : row.lastmodified,
      }
    })
    props.increment();
  }

  return (
    <Stack rowGap={4} maxWidth={"100%"}>
      {loader ? (
        <Loader />
      ) : (
        <>
          <Stack rowGap={4}>
            <GridContainer>
              <Grid item md={6} sm={12} xs={12}>
                <GenericInput
                  textFieldInputProps={{
                    select: true,
                    fullWidth: true,
                    label: "Entity",
                    SelectProps: {
                      native: false,
                      MenuProps: { sx: { maxHeight: 300 } },
                    },
                    value: entity,
                    children: props.entitiesList.map((entity: IntegrationEntity) => (
                      <MenuItem key={entity.entityName} value={entity.entityName}>
                        {entity.entityName}
                      </MenuItem>
                    )),
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Tooltip
                            title="Select entity type for integration"
                            arrow
                          >
                            <InfoOutlinedIcon fontSize="small" />
                          </Tooltip>
                        </InputAdornment>
                      ),
                    },
                    onChange: (e) => setEntity(e.target.value),
                  }}
                />
              </Grid>

              <Grid item md={3} sm={6} xs={12}>
                <GenericInput
                  textFieldInputProps={{
                    select: true,
                    fullWidth: true,
                    label: "Delimiter",
                    value: delimiter,
                    children: props.DelimiterOptions.map(
                      (delimiter: { [key: string]: string }) => (
                        <MenuItem
                          key={Object.keys(delimiter)[0]}
                          value={Object.values(delimiter)[0]}
                        >
                          {Object.keys(delimiter)[0]}
                        </MenuItem>
                      )
                    ),
                    onChange: (e) => setDelimiter(e.target.value),
                  }}
                />
              </Grid>

              <Grid item md={3} sm={6} xs={12}>
                <GenericInput
                  textFieldInputProps={{
                    fullWidth: true,
                    select: true,
                    label: "Encoding",
                    value: encoding,
                    children: props.EncodingOptions.map((encoding) => (
                      <MenuItem key={encoding} value={encoding}>
                        {encoding}
                      </MenuItem>
                    )),
                    onChange: (e) => setEncoding(e.target.value),
                  }}
                />
              </Grid>
            </GridContainer>

            <GridContainer>
              <Grid item sm={12} xs={12} md={6} lg={4} >
                <GenericInput
                  textFieldInputProps={{
                    fullWidth: true,
                    label: "File Prefix Filter",
                    placeholder: "Enter file prefix",
                    value: searchText,
                    onChange: (e) => setSearchText(e.target.value),
                  }}
                />
              </Grid>

              <Grid item sm={12} xs={12} md={6} lg={4} >
                <GenericInput
                  textFieldInputProps={{
                    fullWidth: true,
                    label: "File Password",
                    placeholder: "Enter password",
                    type: "password",
                    value: passkey,
                    onChange: (e) => setPasskey(e.target.value),
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Tooltip
                            title={
                              <div>
                                <b>(only zip files supported)</b>
                                <li>Enter password if file is password protected.</li>
                                <li>Leave blank if no password required.</li>
                              </div>
                            }
                            arrow
                          >
                            <InfoOutlinedIcon fontSize="small" />
                          </Tooltip>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
            </GridContainer>
          </Stack>

          <FileInfoTable
            fileFilterColumnsConfig={props.fileFilterColumnsConfig}
            searchText={searchText}
            directoryInfo={fileMetaDataList}
            apiRef={apiRef}
          />
          
          <Box width={"100%"} display="flex">
            <Button key="previous" className="navigation-btn bottom-bar-navigation-btn" onClick={props.decrement} variant="contained">Previous</Button>
            <Button key="next" type="submit" className="navigation-btn bottom-bar-navigation-btn" onClick={handleClick} variant="contained">Next</Button>
          </Box>
        </>
      )}
    </Stack>
  );
}

function GridContainer(props: { children:JSX.Element|JSX.Element[] }) {
  return (
      <Grid container spacing={3} justifyContent="space-between" alignItems="center" columns={12}>
          {props.children}
      </Grid>
  );
}
